import { Arco } from './Arco';
import { Aurora } from './Aurora';
import { BoldBlock } from './BoldBlock';
import { ClassicCenter } from './ClassicCenter';
import { Editorial } from './Editorial';
import { ElegantFrame } from './ElegantFrame';
import { Estrutura } from './Estrutura';
import { Lombada } from './Lombada';
import { MinimalLine } from './MinimalLine';
import { ModernBand } from './ModernBand';
import { Papelaria } from './Papelaria';
import { SoftHeader } from './SoftHeader';
import type { CoverTemplateProps } from './shared';
import type { CoverFieldKey, CoverTemplateKey } from '@/types/cover';
import type { FC } from 'react';

/**
 * Cover template registry — the single source of truth for what templates
 * exist and how the picker/editor describe them. Adding a template is a new
 * entry here plus its layout component, never an edit to CoverView.
 *
 * The picker offers only COVER_PICKER_KEYS (the premium generation); legacy
 * keys stay registered so covers saved before the redesign keep rendering.
 *
 * `finalidade` (CFP §2º IV) is in every `supportedFields` so restoring it
 * later is a data change, but it is not default-visible and the editor does
 * not offer it — there is no data source in v1.
 */
export type CoverTemplateDefinition = {
    key: CoverTemplateKey;
    /** pt-BR display name, per the approved mockups. */
    name: string;
    description: string;
    /** Native display face when `cover.font` is unset. */
    titleFont: 'serif' | 'sans';
    /** False = art-directed fixed palette; the editor hides the color controls. */
    usesBrandColors: boolean;
    supportsBackgroundImage: boolean;
    supportedFields: CoverFieldKey[];
    Component: FC<CoverTemplateProps>;
};

const ALL_FIELDS: CoverFieldKey[] = [
    'document_title',
    'subtitle',
    'patient_name',
    'solicitante',
    'finalidade',
    'psychologist_name',
    'crp',
    'city_date',
];

export const COVER_TEMPLATES: Record<CoverTemplateKey, CoverTemplateDefinition> = {
    editorial: {
        key: 'editorial',
        name: 'Editorial',
        description: 'Título serifado gigante, muito respiro, um filete.',
        titleFont: 'serif',
        usesBrandColors: true,
        supportsBackgroundImage: false,
        supportedFields: ALL_FIELDS,
        Component: Editorial,
    },
    aurora: {
        key: 'aurora',
        name: 'Aurora',
        description: 'Brilhos suaves em sálvia e azul sobre off-white. Paleta própria.',
        titleFont: 'serif',
        usesBrandColors: false,
        supportsBackgroundImage: false,
        supportedFields: ALL_FIELDS,
        Component: Aurora,
    },
    estrutura: {
        key: 'estrutura',
        name: 'Estrutura',
        description: 'Grid suíço: moldura fina, círculo e meta em colunas.',
        titleFont: 'sans',
        usesBrandColors: true,
        supportsBackgroundImage: false,
        supportedFields: ALL_FIELDS,
        Component: Estrutura,
    },
    arco: {
        key: 'arco',
        name: 'Arco',
        description: 'Arcos suaves na cor primária subindo da base.',
        titleFont: 'serif',
        usesBrandColors: true,
        supportsBackgroundImage: false,
        supportedFields: ALL_FIELDS,
        Component: Arco,
    },
    papelaria: {
        key: 'papelaria',
        name: 'Papelaria',
        description: 'Moldura dupla e creme quente, ar de papelaria fina. Paleta própria.',
        titleFont: 'serif',
        usesBrandColors: false,
        supportsBackgroundImage: false,
        supportedFields: ALL_FIELDS,
        Component: Papelaria,
    },
    lombada: {
        key: 'lombada',
        name: 'Lombada',
        description: 'Assinatura na lombada, título ancorado na base.',
        titleFont: 'serif',
        usesBrandColors: true,
        supportsBackgroundImage: false,
        supportedFields: ALL_FIELDS,
        Component: Lombada,
    },
    classic_center: {
        key: 'classic_center',
        name: 'Clássica centrada',
        description: 'Serifada, simétrica, dois filetes. O padrão seguro.',
        titleFont: 'serif',
        usesBrandColors: true,
        supportsBackgroundImage: false,
        supportedFields: ALL_FIELDS,
        Component: ClassicCenter,
    },
    modern_band: {
        key: 'modern_band',
        name: 'Faixa lateral',
        description: 'Barra vertical na cor primária, texto à esquerda.',
        titleFont: 'sans',
        usesBrandColors: true,
        supportsBackgroundImage: false,
        supportedFields: ALL_FIELDS,
        Component: ModernBand,
    },
    minimal_line: {
        key: 'minimal_line',
        name: 'Linha mínima',
        description: 'Um traço na cor secundária e muito respiro.',
        titleFont: 'sans',
        usesBrandColors: true,
        supportsBackgroundImage: false,
        supportedFields: ALL_FIELDS,
        Component: MinimalLine,
    },
    elegant_frame: {
        key: 'elegant_frame',
        name: 'Moldura',
        description: 'Filete emoldurando a página, ar formal.',
        titleFont: 'serif',
        usesBrandColors: true,
        supportsBackgroundImage: false,
        supportedFields: ALL_FIELDS,
        Component: ElegantFrame,
    },
    bold_block: {
        key: 'bold_block',
        name: 'Bloco sólido',
        description: 'Página inteira na cor primária. Exige logo claro.',
        titleFont: 'sans',
        usesBrandColors: true,
        supportsBackgroundImage: false,
        supportedFields: ALL_FIELDS,
        Component: BoldBlock,
    },
    soft_header: {
        key: 'soft_header',
        name: 'Topo colorido',
        description: 'Cabeçalho na cor primária, corpo claro.',
        titleFont: 'sans',
        usesBrandColors: true,
        supportsBackgroundImage: false,
        supportedFields: ALL_FIELDS,
        Component: SoftHeader,
    },
};

/** The premium generation — the only templates the picker/strip offer. */
export const COVER_PICKER_KEYS: CoverTemplateKey[] = [
    'editorial',
    'aurora',
    'estrutura',
    'arco',
    'papelaria',
    'lombada',
];
